import { useEffect, useState } from 'react';

import {
  Purpose,
  type TrackingConsentDetails,
  type TranscendAPI,
} from '@transcend-io/airgap.js-types';

import type { AirgapAPI } from '@main/ag-types';
import { logger } from '@main/core-ui';
import { switchCase } from '@main/utils';

import { usePartition } from './usePartition';

export interface AirgapApis {
  /** airgap API */
  airgap: AirgapAPI;
  /** consent manager UI API */
  transcend: TranscendAPI;
  /** Current consent preferences */
  trackingConsentDetails: TrackingConsentDetails;
}

export const getConsentPurposeOrder = switchCase<number, [], string>({
  [Purpose.Essential]: 0,
  [Purpose.Functional]: 1,
  [Purpose.Analytics]: 2,
  [Purpose.Advertising]: 3,
  [Purpose.SaleOfInfo]: 4,
  [Purpose.Unknown]: 100,
})(5);

/**
 * Load an airgap bundle
 *
 * @param options - Options
 * @returns Initialized airgap
 */
export function useAirgap({
  bundleURL,
  skip,
  suppress,
}: {
  /** Airgap bundle URL to load */
  bundleURL: string | string;
  /** When true, don't initialize */
  skip: boolean;
  /** Suppress auto show */
  suppress?: boolean;
}): [AirgapApis | undefined, Event | undefined] {
  // load in the consent manager
  const [airgapAPI, setAirgapApi] = useState<AirgapApis | undefined>();
  const [{ selectedPartition }] = usePartition();
  const [loadEvent, setLoadEvent] = useState<Event | undefined>();

  useEffect(() => {
    // do not initialize if feature not enabled
    if (skip) {
      return undefined;
    }

    const selfWithAirgap: {
      /** airgap.js interface */
      airgap?: AirgapAPI;
      // eslint-disable-next-line no-restricted-globals
    } = self as any;

    // re-use initialized airgap
    if (selfWithAirgap.airgap?.initialized) {
      setAirgapApi({
        airgap: selfWithAirgap.airgap,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        transcend: transcend as any,
        trackingConsentDetails: selfWithAirgap.airgap.getConsent(),
      });

      // Callback to re-calculate consent preferences
      const onConsentChange = (): void => {
        setAirgapApi({
          airgap: selfWithAirgap.airgap!,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          transcend: transcend as any,
          trackingConsentDetails: selfWithAirgap.airgap!.getConsent(),
        });
      };

      // Add listener on change to re-calculate consent preferences
      selfWithAirgap.airgap?.addEventListener(
        'consent-change',
        onConsentChange,
      );

      // Remove event listener when component dismounts
      return () =>
        selfWithAirgap.airgap?.removeEventListener(
          'consent-change',
          onConsentChange,
        );
    }

    // initialize airgap
    if (bundleURL) {
      logger.info(`Initializing consent manager from ${bundleURL}`);

      // Stub airgap.js ready queue
      if (!selfWithAirgap?.airgap?.ready) {
        selfWithAirgap.airgap = {
          readyQueue: [],
          /**
           * @param callback - callback
           */
          ready(callback: any) {
            this.readyQueue!.push(callback);
          },
          ...selfWithAirgap?.airgap,
        } as any;
      }

      // Callback to re-calculate consent preferences
      const onConsentChange = (): void => {
        setAirgapApi({
          airgap: selfWithAirgap.airgap!,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          transcend: transcend as any,
          trackingConsentDetails: selfWithAirgap.airgap!.getConsent(),
        });
      };

      // Wait for airgap.js to be ready
      selfWithAirgap.airgap?.ready((airgap) => {
        setAirgapApi({
          airgap,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          transcend: transcend as any,
          trackingConsentDetails: airgap.getConsent(),
        });

        airgap.addEventListener('consent-change', onConsentChange);
      });

      const script = document.createElement('script');

      // Reject promise if airgap.js fails to load
      script.addEventListener('error', (evt) => {
        logger.error(evt);
      });

      // Load airgap.js script asynchronously
      script.dataset.dismissedViewState = 'Closed';
      script.dataset.reportOnly = 'on';
      if (selectedPartition) {
        script.dataset.partition = selectedPartition;
      }
      if (suppress) {
        script.dataset.prompt = '0';
      }
      script.async = true;
      script.defer = true;
      script.src = bundleURL;
      script.onload = (event) => {
        setLoadEvent(event);
      };
      document.documentElement?.appendChild?.(script);
      return undefined;
    }
    return undefined;
  }, [bundleURL, skip]);

  return [airgapAPI, loadEvent];
}
